// Shared by the full-body and head-only mascot canvases: device quality, media queries, offscreen
// pause and the WebGL failure boundary. No GLB is imported here.
import { Component, useEffect, useState, type ReactNode, type RefObject } from "react";
import { QUALITY, type QualityTier } from "./mascot.config";
import type { MascotQuality, MascotTuning } from "./mascot.types";

export const DEFAULT_TUNING: MascotTuning = {
  speed: 1,
  crossfade: null,
  mouth: null,
  blinkNonce: 0,
  replayNonce: 0,
};

function media(query: string): boolean {
  return typeof matchMedia === "function" && matchMedia(query).matches;
}

export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => media(query));
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

export function resolveQuality(quality: MascotQuality): QualityTier {
  if (quality !== "auto") return QUALITY[quality];
  if (media("(pointer: coarse)") || media("(max-width: 767px)")) return QUALITY.mobile;
  return (globalThis.devicePixelRatio ?? 1) > 1.5
    ? QUALITY["desktop-high"]
    : QUALITY["desktop-normal"];
}

/** False while the element is scrolled off screen (the canvas then stops rendering). */
export function useOnScreen(ref: RefObject<HTMLElement | null>): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver !== "function") return;
    const io = new IntersectionObserver(([e]) => setVisible(Boolean(e?.isIntersecting)), {
      rootMargin: "64px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return visible;
}

export class WebGLBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
